import React, { useState } from 'react';
import { classNames } from '@/lib/utils';

// Helper to highlight search matches
function HighlightText({ text, search }: { text: string | null; search: string }) {
  if (!text) return null;
  if (!search) return <span>{text}</span>;
  const lowerText = text.toLowerCase();
  const lowerSearch = search.toLowerCase();
  const index = lowerText.indexOf(lowerSearch);
  
  if (index === -1) return <span>{text}</span>;
  
  return (
    <span>
      {text.substring(0, index)}
      <mark className="bg-aura-primary/20 text-aura-primary font-bold rounded px-0.5 border-b border-aura-primary/30">
        {text.substring(index, index + search.length)}
      </mark>
      {text.substring(index + search.length)}
    </span>
  );
}

interface JsonNodeProps {
  label: string | null;
  value: any;
  search: string;
  defaultOpen: boolean;
  isLast: boolean;
}

function JsonNode({ label, value, search, defaultOpen, isLast }: JsonNodeProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const isObject = value !== null && typeof value === 'object';
  const isArray = Array.isArray(value);

  // Primitive value rendering
  if (!isObject) {
    const stringValue = value === null ? 'null' : value === undefined ? 'undefined' : String(value);
    const valueColor = 
      typeof value === 'string' ? 'text-emerald-500' : 
      typeof value === 'number' ? 'text-amber-500' : 
      typeof value === 'boolean' ? 'text-aura-primary' : 
      'text-aura-text/40';
    
    return (
      <div className="pl-8 py-0.5 font-mono text-[13px] leading-relaxed break-words whitespace-pre-wrap">
        {label && <span className="text-aura-primary/60 font-bold tracking-tight"><HighlightText text={label} search={search} />: </span>}
        <span className={classNames("font-bold", valueColor)}>
          {typeof value === 'string' ? '"' : ''}
          <HighlightText text={stringValue} search={search} />
          {typeof value === 'string' ? '"' : ''}
        </span>
        {!isLast && <span className="text-aura-text/10">,</span>}
      </div>
    );
  }

  const keys = Object.keys(value);
  const isEmpty = keys.length === 0;
  const bracketOpen = isArray ? '[' : '{';
  const bracketClose = isArray ? ']' : '}';

  if (isEmpty) {
    return (
      <div className="pl-8 py-0.5 font-mono text-[13px] leading-relaxed">
        {label && <span className="text-aura-primary/60 font-bold tracking-tight"><HighlightText text={label} search={search} />: </span>}
        <span className="text-aura-text/20">{bracketOpen}{bracketClose}</span>
        {!isLast && <span className="text-aura-text/10">,</span>}
      </div>
    );
  }

  return (
    <div className="pl-5">
      {/* Summary row with +/- indicator */}
      <div 
        className="flex items-center py-0.5 cursor-pointer select-none font-mono text-[13px] leading-relaxed group -ml-1 pl-1"
        onClick={() => setIsOpen(!isOpen)}
      >
        {/* +/- indicator box */}
        <span className="inline-flex w-4 h-4 items-center justify-center text-[10px] font-black text-aura-primary/40 bg-aura-primary/5 border border-aura-primary/10 rounded group-hover:bg-aura-primary group-hover:text-white transition-all mr-2 shrink-0">
          {isOpen ? '−' : '+'}
        </span>
        
        {label && <span className="text-aura-primary/60 font-bold tracking-tight mr-1.5"><HighlightText text={label} search={search} />:</span>}
        <span className="text-aura-text/30 font-bold">{bracketOpen}</span>
        {!isOpen && <span className="text-aura-text/10 text-[11px] ml-2 font-black italic tracking-widest">{keys.length} items</span>}
      </div>

      {/* Content - shown when open */}
      {isOpen && (
        <div className="border-l-2 border-aura-primary/5 ml-[7px] pl-2 animate-in slide-in-from-left-1 duration-300">
          {keys.map((k, idx) => (
            <JsonNode 
              key={k} 
              label={isArray ? null : k} 
              value={value[k as keyof typeof value]} 
              search={search} 
              defaultOpen={defaultOpen}
              isLast={idx === keys.length - 1} 
            />
          ))}
          <div className="pl-8 py-0.5 font-mono text-[13px] text-aura-text/30 font-bold leading-relaxed">
            {bracketClose}
            {!isLast && <span className="text-aura-text/10">,</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export function JsonViewer({ data, searchTerm = '' }: { data: any, searchTerm?: string }) {
  const [expandAllFlag, setExpandAllFlag] = useState(true); // Start expanded by default
  const [treeKey, setTreeKey] = useState(0);

  const handleExpandAll = () => {
    setExpandAllFlag(true);
    setTreeKey(k => k + 1);
  };

  const handleCollapseAll = () => {
    setExpandAllFlag(false);
    setTreeKey(k => k + 1);
  };

  // Auto-expand when search changes
  React.useEffect(() => {
    if (searchTerm) {
      setExpandAllFlag(true);
      setTreeKey(k => k + 1);
    }
  }, [searchTerm]);

  if (data === undefined || data === null) return null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end gap-2">
        <button 
          onClick={handleCollapseAll}
          className="text-[11px] font-bold text-aura-text/40 hover:text-aura-primary uppercase tracking-wider transition-colors bg-aura-text/5 px-3 py-1.5 rounded-lg"
        >
          Collapse All
        </button>
        <button 
          onClick={handleExpandAll}
          className="text-[11px] font-bold text-aura-primary hover:text-aura-primary/80 uppercase tracking-wider transition-colors bg-aura-primary/10 px-3 py-1.5 rounded-lg"
        >
          Expand All
        </button>
      </div>
      
      <div className="bg-aura-background p-5 rounded-2xl border border-aura-text/10 overflow-x-auto min-h-[100px]">
        <JsonNode 
          key={treeKey}
          label={null} 
          value={data} 
          search={searchTerm} 
          defaultOpen={expandAllFlag}
          isLast={true} 
        />
      </div>
    </div>
  );
}
